import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import { useAuth } from '../auth/AuthContext';
import { formatUserName } from '../../hooks/useAdminMetrics.helpers';
import './AdminCallerIdRequests.css';

const COMPLETE_ENDPOINT = 'https://us-central1-maia-tech.cloudfunctions.net/completeManualCallerIdVerification';

const formatPhone = (e164) => {
  if (!e164) return '';
  const digits = e164.replace(/\D/g, '');
  const local = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (local.length !== 10) return e164;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString();
};

const AdminCallerIdRequests = () => {
  const { currentUser } = useAuth();
  const [authzChecked, setAuthzChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [rowError, setRowError] = useState({});
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [loadingVerified, setLoadingVerified] = useState(false);
  const [verifiedError, setVerifiedError] = useState('');

  useEffect(() => {
    if (!currentUser) {
      setAuthzChecked(true);
      return;
    }
    (async () => {
      try {
        const tokenResult = await currentUser.getIdTokenResult();
        setAuthorized(!!tokenResult.claims.isAdmin);
      } catch (err) {
        console.warn('admin authz check failed', err);
        setAuthorized(false);
      } finally {
        setAuthzChecked(true);
      }
    })();
  }, [currentUser]);

  const loadRequests = useCallback(async () => {
    if (!authorized) return;
    setLoadingRequests(true);
    setLoadError('');
    try {
      const q = query(
        collection(db, 'caller_id_verification_requests'),
        where('status', '==', 'pending'),
        orderBy('requested_at', 'asc')
      );
      const docs = await getDocs(q);
      setRequests(docs.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('failed to load requests', err);
      setLoadError('Failed to load requests. Check your admin access.');
    } finally {
      setLoadingRequests(false);
    }
  }, [authorized]);

  useEffect(() => {
    if (authorized) loadRequests();
  }, [authorized, loadRequests]);

  // Verified users: read all user docs and filter to those flagged
  // phone_verified === true. Population is small in beta, so client-side
  // filtering is cheaper than maintaining a composite index for an
  // inequality on phone_verified_number.
  const loadVerifiedUsers = useCallback(async () => {
    if (!authorized) return;
    setLoadingVerified(true);
    setVerifiedError('');
    try {
      const snap = await getDocs(collection(db, 'users'));
      const rows = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((u) => u.phone_verified === true || (u.phone_verified_number && u.phone_verified_number.length > 0))
        .sort((a, b) => formatUserName(a, a.uid).localeCompare(formatUserName(b, b.uid)));
      setVerifiedUsers(rows);
    } catch (err) {
      console.error('failed to load verified users', err);
      setVerifiedError('Failed to load verified users.');
    } finally {
      setLoadingVerified(false);
    }
  }, [authorized]);

  useEffect(() => {
    if (authorized) loadVerifiedUsers();
  }, [authorized, loadVerifiedUsers]);

  const handleMarkVerified = async (request) => {
    setBusyId(request.id);
    setRowError((prev) => ({ ...prev, [request.id]: '' }));
    try {
      await axios.post(COMPLETE_ENDPOINT, {
        admin_uid: currentUser.uid,
        request_id: request.id,
        phone_number: request.phone_number,
      });
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
    } catch (err) {
      const msg = err?.response?.data?.error || 'Request failed';
      setRowError((prev) => ({ ...prev, [request.id]: msg }));
    } finally {
      setBusyId('');
    }
  };

  const twilioDeepLink = useMemo(
    () => 'https://console.twilio.com/us1/develop/phone-numbers/manage/verified',
    []
  );

  if (!authzChecked) return <div className="admin-cir">Checking admin access…</div>;
  if (!authorized) return <div className="admin-cir">Not authorized.</div>;

  return (
    <div className="admin-cir">
      <div className="admin-cir-header">
        <h2>Pending caller-ID verification requests</h2>
        <button type="button" className="admin-cir-refresh" onClick={loadRequests} disabled={loadingRequests}>
          {loadingRequests ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loadError && <p className="admin-cir-error">{loadError}</p>}

      {!loadingRequests && requests.length === 0 && !loadError && (
        <p>No pending requests.</p>
      )}

      {requests.length > 0 && (
        <table className="admin-cir-table">
          <thead>
            <tr>
              <th>Phone</th>
              <th>Email</th>
              <th>Requested</th>
              <th>User ID</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id}>
                <td>{formatPhone(r.phone_number)}</td>
                <td>{r.email || '—'}</td>
                <td>{formatTimestamp(r.requested_at)}</td>
                <td className="admin-cir-uid">{r.uid}</td>
                <td>
                  <a
                    href={twilioDeepLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-cir-link"
                  >
                    Open Twilio
                  </a>
                  <button
                    type="button"
                    className="admin-cir-primary"
                    onClick={() => handleMarkVerified(r)}
                    disabled={busyId === r.id}
                  >
                    {busyId === r.id ? 'Marking…' : 'Mark verified'}
                  </button>
                  {rowError[r.id] && (
                    <div className="admin-cir-row-error" role="alert">{rowError[r.id]}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="admin-cir-hint">
        Workflow: add the user's number in the Twilio console's Verified Caller IDs page, complete the voice verification call there, then click "Mark verified" to flip the user's Firestore doc.
      </p>

      <div className="admin-cir-section">
        <div className="admin-cir-header">
          <h2>Verified caller-IDs</h2>
          <button type="button" className="admin-cir-refresh" onClick={loadVerifiedUsers} disabled={loadingVerified}>
            {loadingVerified ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {verifiedError && <p className="admin-cir-error">{verifiedError}</p>}

        {!loadingVerified && verifiedUsers.length === 0 && !verifiedError && (
          <p>No users with verified caller-ID yet.</p>
        )}

        {verifiedUsers.length > 0 && (
          <table className="admin-cir-table">
            <thead>
              <tr>
                <th>Phone</th>
                <th>Name</th>
                <th>Email</th>
                <th>User ID</th>
              </tr>
            </thead>
            <tbody>
              {verifiedUsers.map((u) => (
                <tr key={u.uid}>
                  <td>{formatPhone(u.phone_verified_number)}</td>
                  <td>{formatUserName(u, u.uid)}</td>
                  <td>{u.email || '—'}</td>
                  <td className="admin-cir-uid">{u.uid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="admin-cir-hint">
          Source: <code>users/&#123;uid&#125;</code> docs where <code>phone_verified === true</code>. Includes
          users verified via this workflow <em>and</em> users verified directly in the Twilio console
          (e.g. account-holder verifications that predate the workflow).
        </p>
      </div>
    </div>
  );
};

export default AdminCallerIdRequests;
